import Phaser from 'phaser';
import { tokens } from '../design/tokens';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from '../dimensions';
import { audio } from '../game/Audio';

const HINT_Y = LOGICAL_HEIGHT * 0.6;

export function showTutorialOverlay(scene: Phaser.Scene, onDismiss: () => void): void {
  const overlay = scene.add.container(0, 0).setDepth(100).setScrollFactor(0);

  const dim = scene.add.rectangle(
    LOGICAL_WIDTH / 2,
    LOGICAL_HEIGHT / 2,
    LOGICAL_WIDTH,
    LOGICAL_HEIGHT,
    0x000000,
    0.55
  );
  overlay.add(dim);

  const hints = hasFinePointer()
    ? makeKeyboardHints(scene)
    : makeDragHints(scene);
  overlay.add(hints);

  scene.tweens.add({
    targets: hints,
    y: '-=8',
    duration: 800,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut'
  });

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    audio.uiTap();
    scene.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: 200,
      onComplete: () => overlay.destroy()
    });
    onDismiss();
  };

  if (hasFinePointer()) {
    waitForBothSidesPressed(scene, dismiss);
  } else {
    scene.input.once('pointerdown', dismiss);
    scene.input.keyboard?.once('keydown-SPACE', dismiss);
  }
}

function waitForBothSidesPressed(scene: Phaser.Scene, done: () => void): void {
  const kb = scene.input.keyboard;
  if (!kb) return;

  let leftPressed = false;
  let rightPressed = false;
  const onLeft = () => { leftPressed = true; check(); };
  const onRight = () => { rightPressed = true; check(); };

  const cleanup = () => {
    kb.off('keydown-W', onLeft);
    kb.off('keydown-S', onLeft);
    kb.off('keydown-UP', onRight);
    kb.off('keydown-DOWN', onRight);
  };
  const check = () => {
    if (leftPressed && rightPressed) {
      cleanup();
      done();
    }
  };

  kb.on('keydown-W', onLeft);
  kb.on('keydown-S', onLeft);
  kb.on('keydown-UP', onRight);
  kb.on('keydown-DOWN', onRight);
  scene.events.once('shutdown', cleanup);
}

function hasFinePointer(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: fine)').matches;
}

function makeDragHints(scene: Phaser.Scene): Phaser.GameObjects.GameObject[] {
  const style: Phaser.Types.GameObjects.Text.TextStyle = {
    fontFamily: tokens.type.ui.family,
    fontSize: `${tokens.type.ui.size + 4}px`,
    color: tokens.colors.ui.text,
    align: 'center',
    fontStyle: '700'
  };
  const left = scene.add.text(LOGICAL_WIDTH * 0.25, HINT_Y, '↕\nDrag here', style).setOrigin(0.5);
  const right = scene.add.text(LOGICAL_WIDTH * 0.75, HINT_Y, '↕\nDrag here', style).setOrigin(0.5);
  return [left, right];
}

function makeKeyboardHints(scene: Phaser.Scene): Phaser.GameObjects.GameObject[] {
  return [
    ...makeKeyPair(scene, LOGICAL_WIDTH * 0.25, HINT_Y, 'W', 'S'),
    ...makeKeyPair(scene, LOGICAL_WIDTH * 0.75, HINT_Y, '↑', '↓'),
  ];
}

function makeKeyPair(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  upLabel: string,
  downLabel: string,
): Phaser.GameObjects.GameObject[] {
  return [
    makeKeyCap(scene, cx, cy - 18, upLabel),
    makeKeyCap(scene, cx, cy + 18, downLabel),
  ];
}

function makeKeyCap(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
): Phaser.GameObjects.Container {
  const container = scene.add.container(x, y);
  const bg = scene.add.rectangle(0, 0, 26, 26, 0x000000, 0.45)
    .setStrokeStyle(1.5, 0xFFE8D6, 0.9)
    .setOrigin(0.5);
  const text = scene.add.text(0, 0, label, {
    fontFamily: tokens.type.ui.family,
    fontSize: `${tokens.type.ui.size + 2}px`,
    color: tokens.colors.ui.text,
    fontStyle: '700',
  }).setOrigin(0.5);
  container.add([bg, text]);
  return container;
}
